import React, { useEffect, useState } from "react";
import { useNavigate, NavLink, Routes, Route } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingCart, Users, Tag, LogOut, TrendingUp, Trash2, Plus, X, Sparkles, FileText, Loader2, Edit2, Upload, ChevronDown, ChevronUp, MapPin, Truck, CreditCard, Phone, Mail } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { fmt, proxyImg } from "../lib/format";

function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (user && !user.roles?.includes("admin")) nav("/");
  }, [user, nav]);

  if (!user) return null;
  if (!user.roles?.includes("admin")) return null;

  const links = [
    { to: "/admin", label: "Dashboard", Icon: LayoutDashboard, end: true },
    { to: "/admin/products", label: "Produtos", Icon: Package },
    { to: "/admin/orders", label: "Pedidos", Icon: ShoppingCart },
    { to: "/admin/users", label: "Usuários", Icon: Users },
    { to: "/admin/coupons", label: "Cupons", Icon: Tag },
  ];

  return (
    <div className="min-h-screen bg-surface-dashboard pt-20" data-testid="admin-shell">
      <div className="max-w-[1400px] mx-auto grid lg:grid-cols-[260px_1fr] gap-6 p-5">
        <aside className="lg:sticky lg:top-24 h-fit bg-white rounded-3xl p-5 border border-black/5">
          <div className="mb-6 px-3">
            <div className="text-xs uppercase tracking-widest text-ink-secondary font-tech mb-1">Painel</div>
            <div className="font-heading text-2xl">Admin</div>
          </div>
          <nav className="space-y-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-full text-sm font-tech transition ${
                    isActive ? "bg-brand-primary text-white" : "hover:bg-bg-secondary"
                  }`
                }
                data-testid={`admin-nav-${l.label.toLowerCase()}`}
              >
                <l.Icon size={16} strokeWidth={1.8} /> {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="border-t border-black/5 mt-6 pt-4 px-3">
            <button
              onClick={async () => { await logout(); nav("/"); }}
              className="flex items-center gap-2 text-sm text-status-error hover:underline"
              data-testid="admin-logout"
            >
              <LogOut size={14} /> Sair
            </button>
          </div>
        </aside>
        <main>{children}</main>
      </div>
    </div>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-heading text-4xl mb-8">Dashboard</h1>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Produtos", value: stats?.total_products, Icon: Package },
          { label: "Pedidos", value: stats?.total_orders, Icon: ShoppingCart },
          { label: "Pagos", value: stats?.paid_orders, Icon: TrendingUp },
          { label: "Usuários", value: stats?.total_users, Icon: Users },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-2xl p-5 border border-black/5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-widest font-tech text-ink-secondary">{s.label}</span>
              <s.Icon size={18} className="text-brand-accent" />
            </div>
            <div className="font-heading text-4xl">{s.value ?? "—"}</div>
          </div>
        ))}
      </div>
      <div className="mt-6 bg-white rounded-2xl p-6 border border-black/5">
        <div className="text-xs uppercase tracking-widest font-tech text-ink-secondary mb-2">Receita total (pedidos pagos)</div>
        <div className="font-heading text-5xl text-brand-primary">{fmt(stats?.revenue || 0)}</div>
      </div>
    </div>
  );
}

/* ─── Product Modal (create + edit) ─────────────────────────────── */
const CATEGORY_OPTIONS = [
  { slug: "saude", name: "Saúde" },
  { slug: "fitness", name: "Fitness" },
  { slug: "bem-estar", name: "Bem-Estar" },
  { slug: "pele", name: "Pele" },
  { slug: "biobeauty", name: "Biobeauty" },
];

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result); // data URL
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function ProductModal({ product, onClose, onSaved }) {
  const isEdit = !!product;
  const [form, setForm] = useState({
    name: product?.name || "",
    price: product?.price || "",
    short_description: product?.short_description || "",
    description: product?.description || "",
    category_slug: product?.category_slugs?.[0] || "saude",
    featured: product?.featured || false,
    stock: product?.stock || 99,
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(product?.thumbnail || null);
  // Default: AI off when editing (admin usually wants to keep their uploaded photo as-is).
  // Default: AI on when creating (admin may want a quick AI mockup if they don't have a photo).
  const [generateAI, setGenerateAI] = useState(!product);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5_000_000) {
      setErr("Imagem muito grande (máx 5MB)");
      return;
    }
    setImageFile(f);
    const dataUrl = await fileToBase64(f);
    setImagePreview(dataUrl);
    setErr("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const cat = CATEGORY_OPTIONS.find((c) => c.slug === form.category_slug);
      const refB64 = imageFile ? await fileToBase64(imageFile) : null;

      if (isEdit) {
        // Update fields
        await api.patch(`/admin/products/${product.id}`, {
          name: form.name,
          price: parseFloat(form.price),
          short_description: form.short_description,
          description: form.description,
          categories: cat ? [cat.name] : product.categories,
          category_slugs: cat ? [cat.slug] : product.category_slugs,
          featured: form.featured,
          stock: parseInt(form.stock, 10),
        });
        // If admin uploaded a new ref or wants AI regen → save image
        if (imageFile || generateAI) {
          if (generateAI) {
            // AI regeneration: backend will create a premium mockup
            await api.post(`/admin/products/${product.id}/generate-image`, {
              name: form.name,
              reference_image_base64: refB64,
            });
          } else if (imageFile) {
            // Save the uploaded image as-is (no AI)
            await api.post(`/admin/products/${product.id}/upload-image`, {
              image_base64: refB64,
            });
          }
        }
      } else {
        await api.post("/admin/products-quick", {
          name: form.name,
          price: parseFloat(form.price),
          short_description: form.short_description,
          description: form.description,
          categories: cat ? [cat.name] : [],
          category_slugs: cat ? [cat.slug] : [],
          featured: form.featured,
          stock: parseInt(form.stock, 10),
          reference_image_base64: refB64,
          generate_image: generateAI,
        });
      }
      onSaved();
      onClose();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Erro ao salvar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="product-modal">
      <div
        className="bg-white rounded-3xl w-full max-w-3xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-black/5 sticky top-0 bg-white">
          <h2 className="font-heading text-3xl">{isEdit ? "Editar Produto" : "Novo Produto"}</h2>
          <button onClick={onClose} className="p-2 hover:bg-bg-secondary rounded-full" data-testid="product-modal-close">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          {/* Image upload */}
          <div className="grid sm:grid-cols-[180px_1fr] gap-5 items-start">
            <div>
              <div className="aspect-square rounded-2xl border-2 border-dashed border-brand-primary/20 bg-bg-secondary grid place-items-center overflow-hidden">
                {imagePreview ? (
                  <img src={imagePreview.startsWith("data:") ? imagePreview : proxyImg(imagePreview)} alt="" className="w-full h-full object-contain" />
                ) : (
                  <div className="text-ink-secondary text-xs text-center px-3">
                    <Upload size={28} className="mx-auto mb-2 opacity-50" />
                    Foto do produto<br/>(opcional)
                  </div>
                )}
              </div>
              <label className="block mt-2 text-center text-xs font-tech text-brand-primary cursor-pointer hover:text-brand-accent">
                <input type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="product-image-input" />
                {imagePreview ? "Trocar imagem" : "Enviar imagem"}
              </label>
            </div>
            <div className="space-y-3">
              <div className={`border rounded-2xl p-4 flex items-start gap-3 transition-colors ${
                generateAI
                  ? "bg-brand-accent/10 border-brand-accent/30"
                  : "bg-bg-secondary border-black/10"
              }`}>
                <Sparkles size={20} className="text-brand-accent flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <div className="font-semibold text-brand-primary mb-1">
                    {generateAI ? "Auto-enhance via IA" : "Salvar imagem original"}
                  </div>
                  <p className="text-ink-secondary text-xs leading-relaxed mb-2">
                    {generateAI ? (
                      <>Ao salvar, geramos uma versão premium da sua foto com a estética da loja (fundo neutro palha, iluminação editorial, label BIOFARMAX).</>
                    ) : (
                      <>Sua foto será salva exatamente como está, sem alterações. Recomendado quando você já tem a imagem final do produto.</>
                    )}
                  </p>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={generateAI} onChange={(e) => setGenerateAI(e.target.checked)} data-testid="generate-ai-toggle" />
                    <span className="font-medium">{generateAI ? "✨ Gerar imagem premium com IA" : "📷 Usar imagem original (sem IA)"}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-ink-secondary font-tech">Nome*</span>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 w-full px-4 py-3 rounded-xl border border-black/10 focus:border-brand-primary outline-none"
                data-testid="product-name-input"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-ink-secondary font-tech">Preço (R$)*</span>
              <input
                required
                type="number"
                step="0.01"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                className="mt-1 w-full px-4 py-3 rounded-xl border border-black/10 focus:border-brand-primary outline-none"
                data-testid="product-price-input"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-ink-secondary font-tech">Categoria</span>
              <select
                value={form.category_slug}
                onChange={(e) => setForm({ ...form, category_slug: e.target.value })}
                className="mt-1 w-full px-4 py-3 rounded-xl border border-black/10 focus:border-brand-primary outline-none bg-white"
                data-testid="product-category-input"
              >
                {CATEGORY_OPTIONS.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-ink-secondary font-tech">Estoque</span>
              <input
                type="number"
                value={form.stock}
                onChange={(e) => setForm({ ...form, stock: e.target.value })}
                className="mt-1 w-full px-4 py-3 rounded-xl border border-black/10 focus:border-brand-primary outline-none"
                data-testid="product-stock-input"
              />
            </label>
          </div>

          <label className="block">
            <span className="text-xs uppercase tracking-widest text-ink-secondary font-tech">Descrição curta</span>
            <input
              value={form.short_description}
              onChange={(e) => setForm({ ...form, short_description: e.target.value })}
              placeholder="Ex: Colágeno hidrolisado premium com vitaminas"
              className="mt-1 w-full px-4 py-3 rounded-xl border border-black/10 focus:border-brand-primary outline-none"
              data-testid="product-short-desc-input"
            />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-ink-secondary font-tech">Descrição completa</span>
            <textarea
              rows={4}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-1 w-full px-4 py-3 rounded-xl border border-black/10 focus:border-brand-primary outline-none resize-y"
              data-testid="product-desc-input"
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="product-featured-input" />
            <span>Produto em destaque (aparece na Home)</span>
          </label>

          {err && (
            <div className="bg-status-error/10 border border-status-error/30 text-status-error rounded-xl px-4 py-3 text-sm" data-testid="product-modal-error">
              {err}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-outline" data-testid="product-modal-cancel">Cancelar</button>
            <button type="submit" disabled={loading} className="btn-primary disabled:opacity-60" data-testid="product-modal-save">
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {generateAI ? "Gerando imagem (15-30s)..." : "Salvando..."}
                </>
              ) : (
                <>{isEdit ? "Salvar alterações" : "Criar produto"}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Products() {
  const [products, setProducts] = useState([]);
  const [modalProduct, setModalProduct] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [regenId, setRegenId] = useState(null);
  const [search, setSearch] = useState("");

  const load = () => api.get("/products?limit=500").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Excluir produto?")) return;
    await api.delete(`/admin/products/${id}`);
    load();
  };

  const regenerate = async (p) => {
    if (!confirm(`Gerar nova imagem premium para "${p.name}"? Pode levar 15-30s.`)) return;
    setRegenId(p.id);
    try {
      await api.post(`/admin/products/${p.id}/generate-image`, { name: p.name });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Falha ao gerar imagem");
    } finally {
      setRegenId(null);
    }
  };

  const openNew = () => { setModalProduct(null); setModalOpen(true); };
  const openEdit = (p) => { setModalProduct(p); setModalOpen(true); };

  const filtered = products.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <h1 className="font-heading text-4xl">Produtos <span className="text-ink-secondary text-2xl">({products.length})</span></h1>
        <button onClick={openNew} className="btn-primary" data-testid="new-product-btn">
          <Plus size={16} /> Novo Produto
        </button>
      </div>
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Buscar produto..."
        className="w-full max-w-md mb-4 px-4 py-3 rounded-full border border-black/10 focus:border-brand-primary outline-none text-sm"
        data-testid="admin-product-search"
      />
      <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-bg-secondary">
            <tr>
              <th className="text-left p-4 font-tech text-xs uppercase tracking-widest">Produto</th>
              <th className="text-left p-4 font-tech text-xs uppercase tracking-widest">Categoria</th>
              <th className="text-right p-4 font-tech text-xs uppercase tracking-widest">Preço</th>
              <th className="text-center p-4 font-tech text-xs uppercase tracking-widest">Estoque</th>
              <th className="p-4 text-right font-tech text-xs uppercase tracking-widest">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} className="border-t border-black/5 hover:bg-bg-secondary/30">
                <td className="p-4 flex items-center gap-3">
                  <img src={proxyImg(p.thumbnail)} alt="" className="w-12 h-12 rounded-lg bg-bg-secondary p-1 object-contain" />
                  <span className="font-medium">{p.name}</span>
                </td>
                <td className="p-4 text-ink-secondary">{p.categories?.[0] || "—"}</td>
                <td className="p-4 text-right font-tech">{fmt(p.price)}</td>
                <td className="p-4 text-center">{p.stock ?? 99}</td>
                <td className="p-4 text-right whitespace-nowrap">
                  <button
                    onClick={() => regenerate(p)}
                    disabled={regenId === p.id}
                    title="Gerar imagem premium com IA"
                    className="p-2 text-brand-accent hover:bg-brand-accent/10 rounded-full disabled:opacity-50"
                    data-testid={`regen-image-${p.id}`}
                  >
                    {regenId === p.id ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                  </button>
                  <button onClick={() => openEdit(p)} className="p-2 text-brand-primary hover:bg-brand-primary/10 rounded-full" data-testid={`edit-product-${p.id}`}>
                    <Edit2 size={16} />
                  </button>
                  <button onClick={() => remove(p.id)} className="p-2 text-status-error hover:bg-status-error/10 rounded-full" data-testid={`delete-product-${p.id}`}>
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {modalOpen && <ProductModal product={modalProduct} onClose={() => setModalOpen(false)} onSaved={load} />}
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [savingTrack, setSavingTrack] = useState({});
  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const updateStatus = async (oid, status) => {
    await api.patch(`/admin/orders/${oid}`, { status });
    load();
  };

  const updateTracking = async (oid, tracking_code) => {
    setSavingTrack((s) => ({ ...s, [oid]: true }));
    try {
      await api.patch(`/admin/orders/${oid}`, { tracking_code });
      await load();
    } finally {
      setSavingTrack((s) => ({ ...s, [oid]: false }));
    }
  };

  // Resolve backend base URL for prescription download (auth via token)
  const downloadPrescription = async (orderId) => {
    try {
      const base = (await import("../lib/backendUrl")).getBackendUrl();
      const token = localStorage.getItem("bfx_token");
      const r = await fetch(`${base}/api/admin/orders/${orderId}/prescription`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!r.ok) {
        alert(r.status === 404 ? "Pedido sem receita anexada" : "Erro ao baixar receita");
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (e) {
      alert("Erro ao baixar receita: " + e.message);
    }
  };

  const statusColor = (s) => ({
    pending_payment: "bg-amber-100 text-amber-800",
    paid: "bg-emerald-100 text-emerald-800",
    shipped: "bg-blue-100 text-blue-800",
    delivered: "bg-green-100 text-green-800",
    cancelled: "bg-red-100 text-red-800",
  }[s] || "bg-gray-100 text-gray-800");

  const paymentColor = (s) => ({
    approved: "bg-emerald-100 text-emerald-800",
    pending: "bg-amber-100 text-amber-800",
    rejected: "bg-red-100 text-red-800",
    refunded: "bg-gray-100 text-gray-800",
  }[s] || "bg-gray-100 text-gray-800");

  return (
    <div data-testid="admin-orders">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-4xl">Pedidos</h1>
        <div className="text-sm text-ink-secondary font-tech">{orders.length} pedido{orders.length !== 1 ? "s" : ""}</div>
      </div>

      {orders.length === 0 ? (
        <div className="bg-white rounded-2xl border border-black/5 p-12 text-center text-ink-secondary">
          Nenhum pedido ainda.
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const isOpen = expanded === o.order_id;
            const items = o.items || [];
            const addr = o.address || {};
            return (
              <div key={o.order_id} className="bg-white rounded-2xl border border-black/5 overflow-hidden" data-testid={`order-row-${o.order_id}`}>
                <div className="grid grid-cols-12 gap-3 p-4 items-center hover:bg-bg-secondary/50 transition">
                  <div className="col-span-12 md:col-span-3">
                    <div className="font-tech text-xs text-ink-secondary">{o.order_id}</div>
                    <div className="text-xs text-ink-secondary mt-1">{new Date(o.created_at).toLocaleString("pt-BR")}</div>
                  </div>
                  <div className="col-span-12 md:col-span-3">
                    <div className="font-medium">{o.user_name || "—"}</div>
                    <div className="text-xs text-ink-secondary truncate">{o.user_email}</div>
                  </div>
                  <div className="col-span-6 md:col-span-2">
                    <div className="font-tech text-lg">{fmt(o.total || 0)}</div>
                    <div className="text-xs text-ink-secondary">{items.length} item{items.length !== 1 ? "ns" : ""} · {o.payment_method?.toUpperCase() || "—"}</div>
                  </div>
                  <div className="col-span-6 md:col-span-2 flex flex-wrap gap-1">
                    <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full font-tech ${statusColor(o.status)}`}>{o.status}</span>
                    <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full font-tech ${paymentColor(o.payment_status)}`}>{o.payment_status}</span>
                  </div>
                  <div className="col-span-12 md:col-span-2 flex justify-end gap-2">
                    {o.prescription_id && (
                      <button onClick={() => downloadPrescription(o.order_id)} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-brand-accent/10 hover:bg-brand-accent hover:text-white text-brand-accent text-xs font-tech transition" data-testid={`download-prescription-${o.order_id}`}>
                        <FileText size={12} /> Receita
                      </button>
                    )}
                    <button
                      onClick={() => setExpanded(isOpen ? null : o.order_id)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-black/10 hover:border-brand-accent hover:bg-brand-accent hover:text-white text-xs font-tech transition"
                      data-testid={`toggle-order-${o.order_id}`}
                    >
                      {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />} Detalhes
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t border-black/5 bg-bg-secondary/30 p-5 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid={`order-details-${o.order_id}`}>
                    {/* Items */}
                    <div>
                      <h3 className="font-heading text-lg mb-3 flex items-center gap-2"><Package size={16} className="text-brand-accent" /> Produtos ({items.length})</h3>
                      <div className="space-y-2">
                        {items.map((it, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-2 rounded-xl bg-white border border-black/5">
                            {(it.thumbnail || it.image) && <img src={proxyImg(it.thumbnail || it.image)} alt={it.name} className="w-12 h-12 rounded-lg object-cover" />}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">{it.name}</div>
                              <div className="text-xs text-ink-secondary">Qtd: {it.quantity} · {fmt(it.price || 0)} un.</div>
                            </div>
                            <div className="font-tech text-sm">{fmt((it.price || 0) * (it.quantity || 1))}</div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 pt-3 border-t border-black/10 space-y-1 text-sm">
                        <div className="flex justify-between text-ink-secondary"><span>Subtotal:</span><span className="font-tech">{fmt(o.subtotal || 0)}</span></div>
                        {o.discount > 0 && <div className="flex justify-between text-ink-secondary"><span>Desconto cupom:</span><span className="font-tech">- {fmt(o.discount)}</span></div>}
                        {o.pix_discount > 0 && <div className="flex justify-between text-brand-accent"><span>Desconto PIX 5%:</span><span className="font-tech">- {fmt(o.pix_discount)}</span></div>}
                        <div className="flex justify-between text-ink-secondary"><span>Frete ({o.shipping_method}):</span><span className="font-tech">{fmt(o.shipping_cost || 0)}</span></div>
                        <div className="flex justify-between font-heading text-lg pt-2 border-t border-black/10 mt-2"><span>Total:</span><span>{fmt(o.total || 0)}</span></div>
                      </div>
                    </div>

                    {/* Shipping + Contact + Actions */}
                    <div className="space-y-5">
                      <div>
                        <h3 className="font-heading text-lg mb-3 flex items-center gap-2"><MapPin size={16} className="text-brand-accent" /> Endereço de entrega</h3>
                        <div className="bg-white rounded-xl border border-black/5 p-3 text-sm space-y-1">
                          <div className="font-medium">{addr.full_name || o.user_name}</div>
                          <div>{addr.street}, {addr.number}{addr.complement ? ` - ${addr.complement}` : ""}</div>
                          <div>{addr.neighborhood} · {addr.city} - {addr.state}</div>
                          <div className="font-tech text-xs text-ink-secondary">CEP: {addr.cep}</div>
                          {addr.phone && <div className="flex items-center gap-1 mt-2 text-xs"><Phone size={12} /> {addr.phone}</div>}
                          {o.user_email && <div className="flex items-center gap-1 text-xs"><Mail size={12} /> {o.user_email}</div>}
                        </div>
                      </div>

                      <div>
                        <h3 className="font-heading text-lg mb-3 flex items-center gap-2"><Truck size={16} className="text-brand-accent" /> Envio</h3>
                        <div className="bg-white rounded-xl border border-black/5 p-3 space-y-2">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-ink-secondary">Status:</span>
                            <select value={o.status} onChange={(e) => updateStatus(o.order_id, e.target.value)} className="text-xs px-2 py-1 rounded border border-black/10 bg-white font-tech" data-testid={`order-status-${o.order_id}`}>
                              <option value="pending_payment">Aguardando pagamento</option>
                              <option value="paid">Pago</option>
                              <option value="shipped">Enviado</option>
                              <option value="delivered">Entregue</option>
                              <option value="cancelled">Cancelado</option>
                            </select>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              defaultValue={o.tracking_code || ""}
                              placeholder="Código de rastreio"
                              className="flex-1 text-xs px-3 py-1.5 rounded border border-black/10 font-tech"
                              onBlur={(e) => e.target.value !== (o.tracking_code || "") && updateTracking(o.order_id, e.target.value)}
                              data-testid={`tracking-input-${o.order_id}`}
                            />
                            {savingTrack[o.order_id] && <Loader2 size={14} className="animate-spin text-brand-accent" />}
                          </div>
                        </div>
                      </div>

                      <div>
                        <h3 className="font-heading text-lg mb-3 flex items-center gap-2"><CreditCard size={16} className="text-brand-accent" /> Pagamento</h3>
                        <div className="bg-white rounded-xl border border-black/5 p-3 text-xs space-y-1">
                          <div className="flex justify-between"><span className="text-ink-secondary">Método:</span><span className="font-tech uppercase">{o.payment_method}</span></div>
                          <div className="flex justify-between"><span className="text-ink-secondary">Status:</span><span className="font-tech">{o.payment_status}</span></div>
                          {o.mp_payment_id && <div className="flex justify-between"><span className="text-ink-secondary">MP ID:</span><span className="font-tech">{o.mp_payment_id}</span></div>}
                          {o.coupon?.code && <div className="flex justify-between"><span className="text-ink-secondary">Cupom:</span><span className="font-tech">{o.coupon.code}</span></div>}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function UsersPage() {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data)); }, []);
  return (
    <div data-testid="admin-users">
      <h1 className="font-heading text-4xl mb-8">Usuários</h1>
      <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-bg-secondary">
            <tr>
              <th className="text-left p-4 font-tech text-xs uppercase tracking-widest">Nome</th>
              <th className="text-left p-4 font-tech text-xs uppercase tracking-widest">Email</th>
              <th className="text-left p-4 font-tech text-xs uppercase tracking-widest">Provider</th>
              <th className="text-left p-4 font-tech text-xs uppercase tracking-widest">Roles</th>
              <th className="text-left p-4 font-tech text-xs uppercase tracking-widest">Cadastro</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} className="border-t border-black/5">
                <td className="p-4">{u.name}</td>
                <td className="p-4 text-ink-secondary">{u.email}</td>
                <td className="p-4 text-xs">{u.auth_provider}</td>
                <td className="p-4 text-xs">{u.roles?.join(", ")}</td>
                <td className="p-4 text-xs text-ink-secondary">{u.created_at?.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState({ code: "", discount_percent: 10, description: "" });
  const load = () => api.get("/admin/coupons").then((r) => setCoupons(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    await api.post("/admin/coupons", {
      code: form.code.toUpperCase(),
      discount_percent: parseFloat(form.discount_percent),
      discount_value: 0,
      active: true,
      description: form.description,
    });
    setForm({ code: "", discount_percent: 10, description: "" });
    load();
  };

  const remove = async (code) => {
    if (!confirm("Excluir cupom?")) return;
    await api.delete(`/admin/coupons/${code}`);
    load();
  };

  return (
    <div data-testid="admin-coupons">
      <h1 className="font-heading text-4xl mb-8">Cupons</h1>
      <form onSubmit={save} className="bg-white rounded-2xl border border-black/5 p-6 mb-6 grid sm:grid-cols-4 gap-3">
        <input required placeholder="Código" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="px-4 py-3 rounded-xl border border-black/10" data-testid="coupon-code-input" />
        <input required type="number" placeholder="% Desconto" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} className="px-4 py-3 rounded-xl border border-black/10" data-testid="coupon-discount-input" />
        <input placeholder="Descrição" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="px-4 py-3 rounded-xl border border-black/10" data-testid="coupon-desc-input" />
        <button className="btn-primary" data-testid="coupon-save-btn"><Plus size={16} /> Criar</button>
      </form>
      <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-bg-secondary">
            <tr>
              <th className="text-left p-4 font-tech text-xs uppercase tracking-widest">Código</th>
              <th className="text-left p-4 font-tech text-xs uppercase tracking-widest">Desconto</th>
              <th className="text-left p-4 font-tech text-xs uppercase tracking-widest">Descrição</th>
              <th className="text-center p-4 font-tech text-xs uppercase tracking-widest">Status</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {coupons.map((c) => (
              <tr key={c.code} className="border-t border-black/5">
                <td className="p-4 font-tech text-brand-accent">{c.code}</td>
                <td className="p-4">{c.discount_percent}%</td>
                <td className="p-4 text-ink-secondary">{c.description}</td>
                <td className="p-4 text-center">
                  <span className={`text-xs px-3 py-1 rounded-full ${c.active ? "bg-status-success/15 text-status-success" : "bg-bg-tertiary text-ink-secondary"}`}>
                    {c.active ? "Ativo" : "Inativo"}
                  </span>
                </td>
                <td className="p-4 text-right">
                  <button onClick={() => remove(c.code)} className="p-2 text-status-error hover:bg-status-error/10 rounded-full" data-testid={`coupon-delete-${c.code}`}>
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Admin() {
  return (
    <AdminLayout>
      <Routes>
        <Route index element={<Dashboard />} />
        <Route path="products" element={<Products />} />
        <Route path="orders" element={<Orders />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="coupons" element={<Coupons />} />
      </Routes>
    </AdminLayout>
  );
}
